import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

/**
 * Dónde estaba el operario cuando recargó.
 *
 * La ruta ya sobrevive al F5 (`App.tsx` postea la vista en el hash), pero lo
 * que se había hecho **adentro** de la vista no: entrar a un sector del mapa,
 * pasar a la vista 3D, filtrar el stock por estado. Recargar volvía a dejar a
 * todo el mundo en el plano general y sin filtros, y en el depósito recargar
 * pasa seguido —se cuelga el lector, se pierde el contexto de WebGL, se corta
 * el wifi—, así que la penitencia era rehacer el mismo camino cada vez.
 *
 * **Por qué `sessionStorage` y no `localStorage`:** esto guarda *dónde estaba*,
 * no *qué prefiere*. Vive lo que vive la pestaña: recargar lo conserva, cerrar
 * y volver mañana arranca limpio, y dos pestañas abiertas en el mismo mapa no
 * se pisan una a la otra. Un filtro puesto la semana pasada que sigue
 * escondiendo pallets sin que nadie lo recuerde es peor que tener que
 * tildarlo de nuevo.
 *
 * **Qué no va acá:** formularios a medio llenar, avisos, marcas de selección
 * que disparan acciones destructivas (las tablas que se vacían en
 * Mantenimiento) y cualquier cosa que al revivir sola haga algo. Se guarda
 * navegación y filtros; nada que apriete un botón por su cuenta.
 */

const PREFIJO = 'wms:';

/**
 * Lo guardado, o `inicial` si no hay nada usable.
 *
 * Nunca tira: un valor de una versión anterior de la pantalla, un JSON roto a
 * mano o un depósito que no deja leer terminan igual, en el valor por omisión.
 * `aceptar` es el filtro de los enums —«plano» | «iso»— donde un string
 * cualquiera rompería el dibujo en vez de caer parado.
 */
export function leerGuardado<T>(
  deposito: Storage | null,
  clave: string,
  inicial: T,
  aceptar?: (guardado: unknown) => boolean,
): T {
  if (!deposito) return inicial;
  try {
    const crudo = deposito.getItem(PREFIJO + clave);
    if (crudo === null) return inicial;
    const valor: unknown = JSON.parse(crudo);
    if (aceptar && !aceptar(valor)) return inicial;
    return valor as T;
  } catch {
    return inicial;
  }
}

/** Guarda, y si no se puede sigue de largo: es una comodidad, no un dato. */
export function escribirGuardado(deposito: Storage | null, clave: string, valor: unknown): void {
  if (!deposito) return;
  try {
    deposito.setItem(PREFIJO + clave, JSON.stringify(valor));
  } catch {
    // Modo privado, cuota llena o cookies bloqueadas: la pantalla anda igual,
    // sólo que arranca de cero la próxima vez.
  }
}

/**
 * El depósito de la pestaña, o null donde no lo haya.
 *
 * Se pide adentro de la función y no al cargar el módulo porque este archivo
 * llega hasta Node: `npm run check:plano` construye por SSR el módulo del mapa
 * y ahí no hay `window`. Y aun en el navegador, tocar `sessionStorage` puede
 * **tirar** —no devolver null— cuando las cookies de terceros están
 * bloqueadas.
 */
function sesion(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * `useState` que sobrevive a un F5 en la misma pestaña.
 *
 * Se usa igual que `useState`; `inicial` es lo que vale la primera vez o
 * cuando lo guardado no sirve. La clave lleva el nombre de la vista adelante
 * (`racks.sector`, `stock.estado`) para que dos pantallas no se pisen.
 */
export function useEstadoGuardado<T>(
  clave: string,
  inicial: T,
  aceptar?: (guardado: unknown) => boolean,
): [T, Dispatch<SetStateAction<T>>] {
  const [valor, setValor] = useState<T>(() => leerGuardado(sesion(), clave, inicial, aceptar));

  useEffect(() => {
    escribirGuardado(sesion(), clave, valor);
  }, [clave, valor]);

  return [valor, setValor];
}
